import { useCallback, useMemo } from 'react';
import { isCampaignActive } from 'src/components/SponsoredListings/SponsoredListings.util';
import { useAdCampaignsDataContext } from 'src/contexts/AdCampaignsDataContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import {
  getEventPerformerVenue,
  getFormattedEventName,
  getIsStrictlyUSEvent,
} from 'src/utils/eventWithDataUtils';
import { posChangedField, posField } from 'src/utils/posFieldUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  AdCampaignState,
  BiddingMode,
  CampaignManagerClient,
  FlightDuration,
  SponsoredListingsCampaignInput,
} from 'src/WebApiController';

export type BulkBoostFormValues = {
  recurringFlightBudget: number;
  flightDuration: FlightDuration;
  biddingMode: BiddingMode;
  baseBid?: number;
};

export const useBulkBoostActions = () => {
  const { getSelection } = useMultiSelectionContext();
  const { data } = useCatalogDataContext();
  const { allCampaignData, refreshData } = useAdCampaignsDataContext();
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();

  // Get selected virtIds from multi-selection context
  const selectedVirtIds = useMemo(() => {
    return getSelection().groupIds;
  }, [getSelection]);

  // Classify events into buckets
  const { unboostedEvents, pausedEvents, activeEvents, ineligibleEvents } =
    useMemo(() => {
      const unboosted: Array<{ virtId: string; viagId: number }> = [];
      const paused: Array<{
        virtId: string;
        viagId: number;
        campaignId: number;
      }> = [];
      const active: Array<{
        virtId: string;
        viagId: number;
        campaignId: number;
      }> = [];
      const ineligible: string[] = [];

      selectedVirtIds.forEach((virtId) => {
        const eventData = data?.events?.[virtId];
        if (!eventData?.event) return;

        const viagId = eventData.event.viagId;
        if (!viagId) return;

        // Get venue to check if it's a US event
        const { venue } = getEventPerformerVenue(virtId, data);
        if (!getIsStrictlyUSEvent(venue?.ctryCode)) {
          ineligible.push(virtId);
          return;
        }

        // Look up campaigns for this event
        const campaigns = allCampaignData?.[viagId] || [];

        // Find active campaigns
        const activeCampaigns = campaigns.filter((c) => isCampaignActive(c));
        if (activeCampaigns.length > 0) {
          active.push({
            virtId,
            viagId,
            campaignId: activeCampaigns[0].campaignId,
          });
          return;
        }

        // Find paused campaigns (not ended)
        const pausedCampaigns = campaigns.filter(
          (c) => c.campaignState === AdCampaignState.Paused && !c.hasEnded
        );
        if (pausedCampaigns.length > 0) {
          paused.push({
            virtId,
            viagId,
            campaignId: pausedCampaigns[0].campaignId,
          });
          return;
        }

        // Otherwise, it's unboosted
        unboosted.push({ virtId, viagId });
      });

      return {
        unboostedEvents: unboosted,
        pausedEvents: paused,
        activeEvents: active,
        ineligibleEvents: ineligible,
      };
    }, [selectedVirtIds, data, allCampaignData]);

  const executeBulkBoost = useCallback(
    async (formValues: BulkBoostFormValues) => {
      const client = new CampaignManagerClient(activeAccountWebClientConfig);

      // Restart paused campaigns
      for (const { campaignId } of pausedEvents) {
        await tryInvokeApi(
          async () => {
            const response = await client.updateCampaignState(
              campaignId,
              AdCampaignState.Active
            );
            if (!response.success) {
              showErrorDialog('updateCampaignState', {
                message: response.errorMessage || 'Failed to restart campaign',
              });
            }
          },
          (error) => {
            showErrorDialog('updateCampaignState', error);
          }
        );
      }

      // Create new campaigns for unboosted events
      for (const { virtId, viagId } of unboostedEvents) {
        const { event, performer, venue } = getEventPerformerVenue(
          virtId,
          data
        );

        const [mainText, connectText, subText] = getFormattedEventName({
          event: event?.event,
          performer,
          venue,
        });

        const eventNameDisplay =
          `${mainText} ${connectText?.trim()} ${subText}`.trim();

        const input: SponsoredListingsCampaignInput = {
          eventId: viagId,
          campaignId: -1, // negative = new campaign
          biddingMode: posChangedField(formValues.biddingMode),
          baseBid: posChangedField(formValues.baseBid ?? null),
          targetTicketClassIds: posField(null),
          campaignName: posChangedField(
            `${eventNameDisplay} - ${formValues.flightDuration}`
          ),
          flightDuration: posChangedField(formValues.flightDuration),
          recurringFlightBudget: posChangedField(
            formValues.recurringFlightBudget
          ),
          currentFlightBudget: posField(null),
          dateRange: {
            start: new Date().toISOString(),
            end: event?.event?.dates?.start ?? null,
          },
          firstThreeRowsOnly: posField(false),
          editBudgetForActiveFlightOnly: posField(false),
        };

        await tryInvokeApi(
          async () => {
            const result = await client.mergeCampaign(input);
            if (!result.success) {
              showErrorDialog('Sponsored Listing', {
                message: result.errorMessage,
              });
            }
          },
          (error) => {
            showErrorDialog('Sponsored Listing', error, {
              trackErrorData: input,
            });
          }
        );
      }

      // Refresh campaign data
      await refreshData();
    },
    [
      pausedEvents,
      unboostedEvents,
      activeAccountWebClientConfig,
      data,
      showErrorDialog,
      refreshData,
    ]
  );

  const executeBulkPause = useCallback(async () => {
    const client = new CampaignManagerClient(activeAccountWebClientConfig);

    // Pause all active campaigns
    for (const { campaignId } of activeEvents) {
      await tryInvokeApi(
        async () => {
          const response = await client.updateCampaignState(
            campaignId,
            AdCampaignState.Paused
          );
          if (!response.success) {
            showErrorDialog('updateCampaignState', {
              message: response.errorMessage || 'Failed to pause campaign',
            });
          }
        },
        (error) => {
          showErrorDialog('updateCampaignState', error);
        }
      );
    }

    // Refresh campaign data
    await refreshData();
  }, [
    activeEvents,
    activeAccountWebClientConfig,
    showErrorDialog,
    refreshData,
  ]);

  return {
    unboostedCount: unboostedEvents.length,
    pausedCount: pausedEvents.length,
    activeCount: activeEvents.length,
    ineligibleCount: ineligibleEvents.length,
    unboostedEvents,
    pausedEvents,
    activeEvents,
    ineligibleEvents,
    executeBulkBoost,
    executeBulkPause,
  };
};
